import { BUSINESS_TYPES, createTenantSchema, TAX_REGIMES } from '@alicerce/contracts';
import { Alert, Button, Field, Select, TextField } from '@alicerce/ui';
import { useNavigate, useSearchParams } from 'react-router';
import { useZodForm } from '@/lib/form';
import { useSession } from '@/lib/session';
import { AuthLayout } from '../auth/layout';

export const BUSINESS_TYPE_LABEL: Record<(typeof BUSINESS_TYPES)[number], string> = {
  PRODUCTS: 'Vendo produtos',
  SERVICES: 'Presto serviços',
  BOTH: 'Produtos e serviços',
};

export const TAX_REGIME_LABEL: Record<(typeof TAX_REGIMES)[number], string> = {
  MEI: 'MEI',
  SIMPLES_NACIONAL: 'Simples Nacional',
  LUCRO_PRESUMIDO: 'Lucro Presumido',
  LUCRO_REAL: 'Lucro Real',
};

/** Lê o plano montado no site (`?modulos=checkout,inventory&intervalo=YEARLY&pacote=...`). */
export function planFromParams(params: URLSearchParams) {
  const modules = (params.get('modulos') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  if (!modules.length) return undefined;
  const interval = params.get('intervalo') === 'YEARLY' ? ('YEARLY' as const) : ('MONTHLY' as const);
  return { modules, interval, bundleKey: params.get('pacote') || null };
}

export function CreateTenantPage() {
  const { api, me, selectTenant, refreshMe } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plan = planFromParams(params);
  const form = useZodForm(createTenantSchema, {
    tradeName: '',
    legalName: '',
    document: null as string | null,
    businessType: 'PRODUCTS' as (typeof BUSINESS_TYPES)[number],
    taxRegime: null as (typeof TAX_REGIMES)[number] | null,
    plan,
  });

  const onSubmit = form.submit(async (data) => {
    const tenant = await api.post<{ id: string }>(
      '/v1/core/tenants',
      { ...data, legalName: data.legalName || null },
      { noTenant: true },
    );
    await refreshMe();
    selectTenant(tenant.id);
    navigate('/', { replace: true });
  });

  return (
    <AuthLayout
      title="Seu negócio"
      subtitle={me?.tenants.length ? 'Cadastre mais um negócio na sua conta.' : 'Falta pouco: conte sobre o seu negócio. Você começa com 14 dias grátis.'}
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {form.formError && <Alert tone="danger">{form.formError}</Alert>}
        <TextField
          label="Nome do negócio"
          hint="É o nome que aparece na loja e no endereço dela."
          value={form.values.tradeName}
          error={form.errors.tradeName}
          onChange={(e) => form.set('tradeName', e.target.value)}
        />
        <Field label="O que você faz?" error={form.errors.businessType}>
          {(a) => (
            <Select {...a} value={form.values.businessType} onChange={(e) => form.set('businessType', e.target.value as (typeof BUSINESS_TYPES)[number])}>
              {BUSINESS_TYPES.map((t) => (
                <option key={t} value={t}>
                  {BUSINESS_TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <TextField
          label="CPF ou CNPJ (opcional)"
          inputMode="numeric"
          value={form.values.document ?? ''}
          error={form.errors.document}
          onChange={(e) => form.set('document', e.target.value || null)}
        />
        <Field label="Regime tributário (opcional)" error={form.errors.taxRegime}>
          {(a) => (
            <Select
              {...a}
              value={form.values.taxRegime ?? ''}
              onChange={(e) => form.set('taxRegime', (e.target.value || null) as (typeof TAX_REGIMES)[number] | null)}
            >
              <option value="">Ainda não sei</option>
              {TAX_REGIMES.map((t) => (
                <option key={t} value={t}>
                  {TAX_REGIME_LABEL[t]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {plan && (
          <Alert tone="info">
            Plano escolhido no site: {plan.modules.length} módulo{plan.modules.length > 1 ? 's' : ''}, cobrança {plan.interval === 'YEARLY' ? 'anual' : 'mensal'}.
          </Alert>
        )}
        <Button type="submit" className="w-full" loading={form.submitting}>
          Começar
        </Button>
      </form>
    </AuthLayout>
  );
}
